import { FC } from 'react';

import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';

import {
  amountColorClass,
  formatSettlementAmount,
} from '../../utils/coupon-settlement.utils';
import { formatDate } from '../../utils/delivery.utils';
import { statusColor } from '../../utils/rebate-settlement.utils';
import { getSettlementPayoutDate } from '../../utils/settlement.utils';
import { UnifiedMonthlySide, UnifiedSideDoc } from '../../utils/unified-settlement.utils';

// ─────────────────────────────────────────────
// 통합 '월별 합계' 표의 공용 셀 (상조 화면 / 관리자·마리 화면 공유)
//
// 한 행이 여러 정산서를 합친 값이라, 지급일과 상세 진입은 **문서 단위**로 편다:
// - 같은 달을 나눠 지급한 차수는 지급일이 서로 다르므로 각각 표기
// - 아직 지급 전(정산집계)이면 정산월 기준 **지급예정일**을 채워 칸이 비지 않게 한다
// - 상세는 탭 이동 없이 문서별 버튼 → 그 자리에서 모달
// 문서가 너무 많은 경우(월 전체 합계 보기 등)엔 접어서 요약만 보여준다.
// ─────────────────────────────────────────────

/** 문서별로 펼쳐 보여줄 최대 개수 — 넘으면 요약 + 탭 이동으로 접는다 */
const MAX_EXPANDED_DOCS = 3;

/** 문서 라벨 — 차수가 2 이상일 때만 "N차"를 붙인다 (여러 상조가 접혔으면 상조명) */
const docLabel = (
  base: string,
  doc: { seq: number; brandName?: string },
  showBrand: boolean,
): string => {
  if (showBrand && doc.brandName) return `${base} · ${doc.brandName}`;
  return doc.seq > 1 ? `${base} ${doc.seq}차` : base;
};

/** 짧은 문서 라벨 — 금액 줄처럼 폭이 좁은 곳에서 "2차"(또는 상조명)만 */
const shortDocLabel = (doc: UnifiedSideDoc, showBrand: boolean): string =>
  showBrand && doc.brandName ? doc.brandName : `${doc.seq}차`;

/** 지급일 한 줄의 표시값 — 지급 전이면 정산월 기준 예정일(자동)로 채운다 */
const payoutOf = (
  doc: Pick<UnifiedSideDoc, 'status' | 'executedAt'>,
  year: number,
  month: number,
): { paid: boolean; date: Date; derived: boolean } => {
  const paid = doc.status === GiftSettlementStatus.Paid;
  if (doc.executedAt) return { paid, date: doc.executedAt, derived: false };
  // 지급예정일: 정산월 다음달 15일 (업체·상조 공통 도출 규칙)
  return { paid, date: getSettlementPayoutDate(year, month), derived: true };
};

export interface AmountCellProps {
  side?: UnifiedMonthlySide;
  /** 지급예정일 도출용 정산월 */
  year: number;
  month: number;
  /** 상태 라벨 — 화면 계열별 카피(상조 '정산예정' / 관리자 '정산집계') 주입 */
  statusLabel: (s: GiftSettlementStatus) => string;
  /** 여러 상조가 한 행에 접힌 보기 — 문서 라벨에 상조명을 쓴다 */
  showBrand?: boolean;
}

/** 상태 태그 + 지급(예정)일 — 금액 바로 옆에 붙여 "언제 얼마"가 한 덩어리로 읽히게 */
const StatusDateTag: FC<{
  label: string;
  status: GiftSettlementStatus;
  date: Date;
  derived: boolean;
}> = ({ label, status, date, derived }) => (
  <span className="flex items-center gap-1 whitespace-nowrap text-[11px]">
    <span className={`rounded-full px-1.5 py-0.5 font-medium ${statusColor(status)}`}>
      {label}
    </span>
    <span
      className={`tabular-nums ${derived ? 'text-gray-400' : 'text-gray-600'}`}
      title={derived ? '정산월 다음달 15일 기준 예정일(자동)' : '기록된 지급(예정)일'}
    >
      {formatDate(date)}
    </span>
  </span>
);

/**
 * 정산금 셀 — 금액 + [상태 태그 · 지급(예정)일]을 한 덩어리로 보여준다.
 * 같은 달을 나눠 지급한 차수가 있으면 **차수별 금액·태그·날짜를 각각** 펼친다
 * (1차는 이미 입금, 2차는 예정 — 이게 한눈에 보여야 한다).
 * 문서가 너무 많으면 합계 + 건수만 표기한다.
 */
export const AmountCell: FC<AmountCellProps> = ({
  side,
  year,
  month,
  statusLabel,
  showBrand = false,
}) => {
  if (!side) return <span className="text-gray-300">-</span>;

  const expanded = side.docs.length > 1 && side.docs.length <= MAX_EXPANDED_DOCS;

  return (
    <div className="flex flex-col items-end gap-0.5">
      <span
        className={`font-medium tabular-nums ${amountColorClass(side.amount)} ${
          expanded ? 'text-base font-semibold' : ''
        }`}
      >
        {formatSettlementAmount(side.amount)}
      </span>

      {/* 문서가 하나면 그 문서의 상태·지급일 */}
      {side.docs.length === 1 && side.docs[0] && (
        <StatusDateTag
          label={statusLabel(side.docs[0].status)}
          status={side.docs[0].status}
          date={payoutOf(side.docs[0], year, month).date}
          derived={payoutOf(side.docs[0], year, month).derived}
        />
      )}

      {/* 분할 지급 — 차수별 금액 + 상태·날짜 */}
      {expanded &&
        side.docs.map((d) => {
          const at = payoutOf(d, year, month);
          return (
            <span key={d.id} className="flex items-center gap-1 text-[11px]">
              <span className="text-gray-400">{shortDocLabel(d, showBrand)}</span>
              <span className={`tabular-nums ${amountColorClass(d.amount)}`}>
                {formatSettlementAmount(d.amount)}
              </span>
              <StatusDateTag
                label={statusLabel(d.status)}
                status={d.status}
                date={at.date}
                derived={at.derived}
              />
            </span>
          );
        })}

      {/* 너무 많으면 상태 + 건수만 */}
      {side.docs.length > MAX_EXPANDED_DOCS && (
        <>
          <span
            className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${statusColor(
              side.status,
            )}`}
          >
            {statusLabel(side.status)}
          </span>
          <span className="text-[11px] text-gray-400">정산서 {side.docCount}건</span>
        </>
      )}
    </div>
  );
};

export interface DetailButtonsProps {
  delivery?: UnifiedMonthlySide;
  coupon?: UnifiedMonthlySide;
  showBrand?: boolean;
  /** 정산서 상세 모달 열기 (탭 이동 없음) */
  onOpenDoc: (kind: 'delivery' | 'coupon', docId: string) => void;
  /** 문서가 많아 접었을 때의 대체 동작 — 해당 종류 탭으로 이동 */
  onOpenTab: (kind: 'delivery' | 'coupon') => void;
}

/** 상세 셀 — 문서별 버튼(모달). 문서가 많으면 탭 이동 버튼 하나로 접는다 */
export const DetailButtons: FC<DetailButtonsProps> = ({
  delivery,
  coupon,
  showBrand = false,
  onOpenDoc,
  onOpenTab,
}) => {
  const cls =
    'rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-30';

  const renderSide = (
    kind: 'delivery' | 'coupon',
    base: string,
    side?: UnifiedMonthlySide,
  ) => {
    if (!side) {
      return (
        <button key={kind} disabled className={cls}>
          {base}
        </button>
      );
    }
    if (side.docs.length > MAX_EXPANDED_DOCS) {
      return (
        <button key={kind} onClick={() => onOpenTab(kind)} className={cls}>
          {base} {side.docs.length}건
        </button>
      );
    }
    return side.docs.map((d) => (
      <button key={d.id} onClick={() => onOpenDoc(kind, d.id)} className={cls}>
        {docLabel(base, d, showBrand)}
      </button>
    ));
  };

  return (
    <div className="flex flex-wrap justify-center gap-1.5">
      {renderSide('delivery', '배송형', delivery)}
      {renderSide('coupon', '쿠폰형', coupon)}
    </div>
  );
};
